import { EMBRYO_FREEZING_PRE_CYCLE_SECTION_IDS } from "@src/packages/homepage/experience";
import { useTranslations } from "@src/packages/i18n";
import type { PlanAction, PlanStep } from "../plan.types";
import { PreCycleHome, type PreCycleHomeSection } from "./PreCycleHome";
import type { PreCyclePrepStep } from "./PreCycleHeroCard";
import type { HeroCycleEnding } from "./hero/Hero";
import { EggFreezingKnowYourNumbersTimelineStepSectionCard } from "./EggFreezingKnowYourNumbersCard";
import { UnderstandEmbryoFreezingStep } from "./EmbryoFreezingUnderstandStep";
import { OptimizeInputsStep } from "./EmbryoFreezingOptimizationStep";
import { FindEmbryoCareStep } from "./EmbryoFreezingClinicStep";
import { StartEmbryoCycleStep } from "./EmbryoFreezingStartCycleStep";

type Props = {
  activeStep: PlanStep | null;
  steps: PreCyclePrepStep[];
  completedSteps: number;
  totalSteps: number;
  userAge?: number;
  /** A partner reading the patient's plan: partner wording, no patient-only steps. */
  viewerIsSupporting?: boolean;
  onAction: (action: PlanAction) => void;
  onOpenPlan: () => void;
  onEditPlan: () => void;
  /** A cycle that has just ended, announced by the hero rather than above it. */
  cycleEnding?: HeroCycleEnding | null;
};

export function PreCycleEmbryoFreezingHome({
  activeStep,
  steps,
  completedSteps,
  totalSteps,
  userAge,
  viewerIsSupporting = false,
  onAction,
  onOpenPlan,
  onEditPlan,
  cycleEnding,
}: Props) {
  const t = useTranslations();
  const heroContent = {
    eyebrow: t("homepage.pre_cycle_embryo_freezing.eyebrow"),
    title: t("homepage.pre_cycle_embryo_freezing.title"),
    cycleStartLabel: t("homepage.pre_cycle_embryo_freezing.cycle_start_label"),
    phaseDescription: t("homepage.pre_cycle_embryo_freezing.phase_description"),
    intro: t("homepage.pre_cycle_embryo_freezing.intro"),
    openPlanAccessibilityLabel: t(
      "homepage.pre_cycle_embryo_freezing.open_plan_a11y",
    ),
    planAccessibilityLabel: t(
      "homepage.pre_cycle_embryo_freezing.toggle_plan_a11y",
    ),
  };
  const sections: PreCycleHomeSection[] = [
    {
      id: EMBRYO_FREEZING_PRE_CYCLE_SECTION_IDS.understand,
      progressStepIds: ["understand"],
      render: () => (
        <UnderstandEmbryoFreezingStep userAge={userAge} onAction={onAction} />
      ),
    },
    {
      id: EMBRYO_FREEZING_PRE_CYCLE_SECTION_IDS.knowYourNumbers,
      progressStepIds: ["know-your-numbers"],
      render: () => (
        <EggFreezingKnowYourNumbersTimelineStepSectionCard
          onAction={onAction}
          showRail={false}
          variant="embryo-freezing"
          viewerIsSupporting={viewerIsSupporting}
        />
      ),
    },
    {
      id: EMBRYO_FREEZING_PRE_CYCLE_SECTION_IDS.optimizeInputs,
      progressStepIds: ["optimize"],
      render: () => <OptimizeInputsStep onAction={onAction} />,
    },
    {
      id: EMBRYO_FREEZING_PRE_CYCLE_SECTION_IDS.findCare,
      progressStepIds: ["find-care"],
      render: () => <FindEmbryoCareStep onAction={onAction} />,
    },
    {
      id: EMBRYO_FREEZING_PRE_CYCLE_SECTION_IDS.startCycle,
      includeInPlanSetup: false,
      patientOnly: true,
      progressStepIds: ["start-cycle"],
      render: () => <StartEmbryoCycleStep onAction={onAction} />,
    },
  ];

  return (
    <PreCycleHome
      activeStep={activeStep}
      steps={steps}
      completedSteps={completedSteps}
      totalSteps={totalSteps}
      eyebrow={heroContent.eyebrow}
      title={heroContent.title}
      snapshotItems={[
        {
          label: t("homepage.pre_cycle_egg_freezing.next"),
          value:
            activeStep?.title ??
            t("homepage.pre_cycle_egg_freezing.choose_next_step"),
        },
        {
          label: t("homepage.pre_cycle_egg_freezing.treatment_cycle"),
          value: t("homepage.pre_cycle_egg_freezing.no_treatment_scheduled"),
          action: {
            label: t("homepage.pre_cycle_egg_freezing.add_treatment_dates"),
            signal: "open-start-treatment-modal",
            params: { presetCycleType: "embryo_freezing" },
          },
        },
      ]}
      phaseDescription={heroContent.phaseDescription}
      intro={heroContent.intro}
      openPlanAccessibilityLabel={heroContent.openPlanAccessibilityLabel}
      planAccessibilityLabel={heroContent.planAccessibilityLabel}
      onAction={onAction}
      onOpenPlan={onOpenPlan}
      onEditPlan={onEditPlan}
      viewerIsSupporting={viewerIsSupporting}
      cycleEnding={cycleEnding}
      showStartCycleAction
      startCyclePreset="embryo_freezing"
      sections={sections}
    />
  );
}
